import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../services/api";
import { toast } from "react-toastify";
import DropdownWithCustomValuesModal from "./DropdownWithCustomValuesModal";
import NotificationEventTypes from "../constants/notificationEventsEnum";

const TagModal = ({
  tagName,
  tagComments,
  tagPriority,
  onSave,
  onClose,
  documentId,
  studentFirstRegistrationId,
  fileName,
}) => {
  const roleId = localStorage.getItem("roleId");
  const token = localStorage.getItem("token");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const [customDropdownName, setCustomDropdownName] = useState("");

  const [tagPriorityData, setTagPriorityData] = useState([]);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail || []);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("tagPriority", setTagPriorityData);
  }, []);

  const isParentRole = ["9", "10", "11"].includes(roleId);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      tagName: tagName || "",
      tagComments: tagComments || "",
      tagPriority: tagPriority || null,
    },
  });

  useEffect(() => {
    setValue("tagName", tagName || "");
    setValue("tagComments", tagComments || "");
    setValue("tagPriority", tagPriority ? String(tagPriority) : "");
  }, [tagName, tagComments, tagPriority, tagPriorityData, setValue]);

  const onSubmit = async (data) => {
    const formData = {
      studentFirstRegistrationId,
      documentId,
      tagName: data.tagName,
      tagComments: data.tagComments,
      tagPriority: data.tagPriority ? parseInt(data.tagPriority) : null,
      FormId: 75,
      NotificationEventTypeIds: NotificationEventTypes.REQUEST.toString(),
    };

    // console.log(formData);
    // return;

    try {
      const response = await post(
        "CommonSettings/AddFirstRegistrationDocumentTag",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        onSave(data);
        reset();
      } else {
        toast.error(response.message || "Failed to save tag data");
      }
    } catch (error) {
      console.error("Error saving tag data:", error);
      toast.error("An error occurred while saving tag data");
    }
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">
                Tag Document{" "}
                <span className="badge bg-primary fs-10">{fileName}</span>
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Tag Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("tagName", {
                        required: !isParentRole && "This field is required.",
                      })}
                      disabled={isParentRole}
                    />
                    {errors.tagName && (
                      <small className="text-danger">
                        {errors.tagName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Tag Priority <span className="text-danger fs-9">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <div className="w-100">
                        <select
                          {...register("tagPriority", {
                            required:
                              !isParentRole && "This field is required.",
                          })}
                          className="form-select"
                          disabled={isParentRole}
                        >
                          <option value="">Select</option>
                          {tagPriorityData.map((item, index) => (
                            <option key={index} value={String(item.id)}>
                              {item.value}
                            </option>
                          ))}
                        </select>
                        {errors.tagPriority && (
                          <small className="text-danger">
                            This field is required.
                          </small>
                        )}
                      </div>
                      {isParentRole ||
                      ["9", "10", "11"].includes(roleId) ? null : (
                        <div>
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            style={{
                              padding: "5.5px 10px",
                            }}
                            onClick={() =>
                              handleCustomDropdownSetting("tagPriority")
                            }
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">
                      Comments<span className="text-danger fs-9">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      {...register("tagComments", {
                        required: !isParentRole && "This field is required.",
                      })}
                      disabled={isParentRole}
                    ></textarea>
                    {errors.tagComments && (
                      <small className="text-danger">
                        {errors.tagComments.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {!isParentRole && (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary btn-sm"
                  >
                    Save
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>

      {isCustomDropdownValuesModalVisible &&
        createPortal(
          <DropdownWithCustomValuesModal
            dropdownName={customDropdownName}
            onClose={() => {
              setIsCustomDropdownValuesModalVisible(false);
              if (customDropdownName === "tagPriority") {
                fetchCommonSettings("tagPriority", setTagPriorityData);
              }
            }}
          />,
          document.body // or a #modal-root div
        )}
    </>
  );
};

export default TagModal;
